import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { Mail, Send, FileText, BarChart3, Settings as SettingsIcon, Rocket, Users, Filter, Loader2, Tag } from "lucide-react";
import { api } from "@/lib/api";
import BulkEmailModal from "@/components/admin/BulkEmailModal";
import { AdminEmailsBody } from "./AdminEmails";
import { AdminMailshotsBody } from "./AdminMailshots";
import { TemplatesTab, NewsletterTab } from "./AdminEmailings";

const TABS = [
  { id: "campaigns", label: "Campagnes", icon: Rocket },
  { id: "segments", label: "Segments", icon: Tag },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "history", label: "Historique & Stats", icon: BarChart3 },
  { id: "config", label: "Configuration", icon: SettingsIcon },
];

function SegmentsTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total_contacts: 0, groups: [] });
  const [pickedTag, setPickedTag] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contacts/segments");
      setData(r.data || { total_contacts: 0, groups: [] });
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const retag = async () => {
    if (!window.confirm("Recalculer les tags automatiques pour TOUS les contacts ? (préserve les tags personnalisés)")) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/contacts/retag");
      await load();
      window.alert(`✅ ${r.data?.retagged || 0} contact(s) retagué(s) sur ${r.data?.total_processed || 0}.`);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const launch = (tag) => { setPickedTag(tag); setOpenModal(true); };

  return (
    <div className="space-y-6" data-testid="emailing-segments-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-wine-100 bg-wine-50/30 p-4">
        <div>
          <h3 className="text-lg font-bold text-navy"><Tag className="mr-1.5 inline h-4 w-4 text-wine-500" /> Segments de contacts</h3>
          <p className="text-xs text-neutral-600">Sélectionnez un segment pour lancer un emailing ciblé (langue, rôle, pôle, source, consentement…). {data.total_contacts} contact{data.total_contacts > 1 ? "s" : ""} au total.</p>
        </div>
        <button onClick={retag} disabled={loading} className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-white px-3 py-1.5 text-xs font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-50" data-testid="retag-contacts-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />} Recalculer les tags auto
        </button>
      </div>

      {loading && data.groups.length === 0 && (
        <div className="rounded-xl border border-neutral-100 bg-white p-8 text-center text-sm text-neutral-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Chargement des segments…</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {data.groups.map((g) => (
          <div key={g.key} className="rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`segment-group-${g.key}`}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">{g.label}</div>
            <div className="space-y-1.5">
              {g.values.map((v) => (
                <button
                  key={v.value}
                  onClick={() => launch(v.value)}
                  disabled={!v.count}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition hover:bg-wine-50/40 ${v.count ? "border-neutral-200" : "border-neutral-100 opacity-50"}`}
                  data-testid={`segment-launch-${v.value}`}
                  title={v.count ? `Lancer un emailing vers le segment ${v.label}` : "Aucun contact dans ce segment"}
                >
                  <span className="font-medium text-neutral-800">{v.label}</span>
                  <span className="inline-flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${v.count ? "bg-wine-100 text-wine-700" : "bg-neutral-100 text-neutral-400"}`}>{v.count}</span>
                    {v.count > 0 && <Send className="h-3 w-3 text-wine-500" />}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <BulkEmailModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        entity="contacts"
        ids={[]}
        typeFilter="contact"
        allowSegment
        initialSegmentTag={pickedTag}
        onSent={() => { setOpenModal(false); load(); }}
      />
    </div>
  );
}

function CampaignsTab() {
  const [mode, setMode] = useState("newsletter"); // newsletter | mailshot
  return (
    <div>
      <div className="mb-5 inline-flex rounded-full border border-neutral-200 bg-neutral-50 p-1 text-sm">
        <button
          onClick={() => setMode("newsletter")}
          className={`rounded-full px-4 py-1.5 font-semibold ${mode === "newsletter" ? "bg-wine-500 text-white shadow" : "text-neutral-600 hover:text-navy"}`}
          data-testid="campaigns-mode-newsletter"
        >
          <Send className="mr-1 inline h-3.5 w-3.5" /> Newsletter Builder
        </button>
        <button
          onClick={() => setMode("mailshot")}
          className={`rounded-full px-4 py-1.5 font-semibold ${mode === "mailshot" ? "bg-wine-500 text-white shadow" : "text-neutral-600 hover:text-navy"}`}
          data-testid="campaigns-mode-mailshot"
        >
          <Users className="mr-1 inline h-3.5 w-3.5" /> Mailshot CRM (one-off)
        </button>
      </div>
      {mode === "newsletter" ? <NewsletterTab /> : <AdminMailshotsBody hideHeader />}
    </div>
  );
}

export default function AdminEmailing() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get("tab") || "campaigns";
  const [tab, setTab] = useState(TABS.find((t) => t.id === initial) ? initial : "campaigns");

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next, { replace: true });
  }, [tab]); // params/setParams are stable - safe to omit

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">
            <Mail className="mr-2 inline h-7 w-7 text-wine-500" />Emailing
          </h1>
          <p className="mt-1 text-neutral-600">
            Hub unifié : campagnes (Newsletter & Mailshot CRM), templates, historique &amp; statistiques, configuration Brevo.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-neutral-200" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative px-4 py-2.5 text-sm font-semibold transition-colors ${active ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`}
              data-testid={`emailing-tab-${t.id}`}
              role="tab"
              aria-selected={active}
            >
              <Icon className="mr-1.5 inline h-4 w-4" />
              {t.label}
              {active && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === "campaigns" && <CampaignsTab />}
        {tab === "segments" && <SegmentsTab />}
        {tab === "templates" && <TemplatesTab />}
        {tab === "history" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4 text-sm text-amber-800">
              📊 L&apos;historique des campagnes Mailshot et l&apos;activité Brevo détaillée sont visibles ci-dessous. Pour suivre une newsletter en cours d&apos;envoi, ouvrez-la dans l&apos;onglet Campagnes — le statut se met à jour en temps réel.
            </div>
            {/* Mailshot history reuses the existing Mailshot body which has its own internal tab */}
            <AdminMailshotsBody hideHeader />
          </div>
        )}
        {tab === "config" && <AdminEmailsBody />}
      </div>

      <div className="mt-10 rounded-2xl border border-neutral-100 bg-neutral-50/60 p-4 text-xs text-neutral-500">
        💡 Astuce : les anciens liens <code className="rounded bg-white px-1.5 py-0.5">/admin/emails</code>, <code className="rounded bg-white px-1.5 py-0.5">/admin/mailshots</code> et <code className="rounded bg-white px-1.5 py-0.5">/admin/emailings</code> redirigent ici automatiquement.
        <button onClick={() => navigate("/admin/emailing")} className="ml-3 text-wine-700 hover:underline">Lien direct</button>
      </div>
    </PortalLayout>
  );
}
